/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import { useEffect, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getBrand } from '../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import useExperience from '../../../../../helpers/Personalization/useExperience';

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// Helper to add padding bottom to the body when sticky ATC is enabled
export const useStickyAtcBodyPadding = (shouldAddPadding, paddingBottom = '80px') => {
    useIsomorphicLayoutEffect(() => {
        if (typeof document === 'undefined') {
            return undefined;
        }

        if (!shouldAddPadding) {
            document.body.style.removeProperty('padding-bottom');
            return undefined;
        }

        document.body.style.paddingBottom = paddingBottom;

        return () => {
            document.body.style.removeProperty('padding-bottom');
        };
    }, [shouldAddPadding, paddingBottom]);
};

const normalizeExperimentValue = (value) => value?.toString?.().trim?.().toLowerCase?.().replace(/[\s_]+/g, '-');

const useStickyAtcExperiment = ({
    isEnabled,
    shouldTrackImpression = true,
}) => {
    const dispatch = useDispatch();
    const brand = useSelector(getBrand);
    const { loading, data } = useExperience({
        interactionName: `${brand?.domain || '1800flowers'}-mobile-pdp-stickyatc-v3`,
        type: 'product',
        apolloOptions: isEnabled ? {} : { skip: true },
    });
    const experiment = data?.experience?.experiment;
    const experimentTracking = data?.experience?.experimentTracking;
    const experimentValue = experiment?.value;
    const stickyAtcValue = typeof experimentValue === 'object'
        ? experimentValue?.stickyAtcEnabled ?? experimentValue?.enabled ?? experimentValue?.showStickyAtc
        : experimentValue;
    const variant = experimentTracking?.variant || experimentTracking?.name || experiment?.name || '';
    const normalizedStickyAtcValue = normalizeExperimentValue(stickyAtcValue);
    const normalizedVariant = normalizeExperimentValue(variant);
    const enabledStickyAtcValues = ['on', 'on-zip', 'true', 'enabled'];
    const enabledStickyAtcVariants = ['variant', 'test', 'true', 'on', 'enabled'];
    const isStickyAtcVariant = typeof stickyAtcValue === 'boolean'
        ? stickyAtcValue
        : enabledStickyAtcValues.includes(normalizedStickyAtcValue)
            || enabledStickyAtcVariants.includes(normalizedVariant);
    const stickyAtcRequiresSavedZip = normalizedStickyAtcValue === 'on-zip';
    const stickyAtcCampaignId = experimentTracking?.campaignId;
    const stickyAtcExperimentReady = !isEnabled || !loading;
    let stickyAtcExperimentLabel = 'Control';
    if (stickyAtcRequiresSavedZip) {
        stickyAtcExperimentLabel = 'Variant 2';
    } else if (isStickyAtcVariant) {
        stickyAtcExperimentLabel = 'Variant 1';
    }
    const stickyAtcEnabled = isEnabled
        && stickyAtcExperimentReady
        && isStickyAtcVariant;

    useEffect(() => {
        if (
            !isEnabled
            || !shouldTrackImpression
            || !stickyAtcExperimentReady
            || !stickyAtcCampaignId
            || typeof window === 'undefined'
            || window.wuStickyATCEventFired
        ) {
            return undefined;
        }

        dispatch(trackEvent({
            eventCategory: 'Experiment',
            eventAction: 'Sticky ATC',
            eventLabel: stickyAtcExperimentLabel,
            feature_detail: stickyAtcExperimentLabel,
            feature_element: 'Sticky ATC',
            feature_category: 'Experiment',
            experiment_id: stickyAtcCampaignId,
        }));
        window.wuStickyATCEventFired = true;

        return undefined;
    }, [dispatch, isEnabled, shouldTrackImpression, stickyAtcCampaignId, stickyAtcExperimentLabel, stickyAtcExperimentReady]);

    return {
        stickyAtcEnabled,
        stickyAtcRequiresSavedZip,
        stickyAtcExperimentLabel,
        stickyAtcExperimentReady,
        experimentTracking,
    };
};

export default useStickyAtcExperiment;
